<template src="./templates/App.html" />

<script setup>

import {
    ref, onMounted, watch, computed,
} from 'vue';
import { useHead } from 'unhead';
import {
    categoryFacets,
    categoryFilters,
    regionFilters,
    searchIndex,
    pageSeoData,
} from './config/config';
import { getBlogBaseURI, getCategoryParamFromURL, getUtmParams } from '../../utils/blogUtils';

import SearchBox from '../blog/components/SearchBox.vue';
import FilterHeader from '../blog/components/FilterHeader.vue';
import FilterPopup from '../blog/components/FilterPopup.vue';
import ResultsCount from '../blog/components/ResultsCount.vue';
import GridView from './components/GridView.vue';
import Pagination from '../blog/components/Pagination.vue';
import ListView from './components/ListView.vue';

const blogTitleDom = document.getElementById('blog-title');
const originalBlogTitle = blogTitleDom ? blogTitleDom.dataset.title : '';
const blogTaglineDom = document.getElementById('blog-tagline');

// Data Properties
const isLoading = ref(true);
const layoutType = ref('grid');
const searchQuery = ref('');
const categoriesFacets = ref([]);
const regionsFacets = ref([]);
const siteMode = ref('');
const selectedCategory = ref('');
const selectedRegion = ref('');
const results = ref([]);
const groupedItems = ref({});
const currentPage = ref(1);
const totalPages = ref(0);
const totalResults = ref(0);

const regionInfo = {};

const getCatIDFromURL = () => {

    const slug = getCategoryParamFromURL();
    const category = categoryFilters.find((cat) => cat.slug === slug);
    return category ? category.category : '';

};

const getCatSlugFromID = () => {

    if (!selectedCategory.value) return '';
    const category = categoryFilters.find((cat) => cat.category === selectedCategory.value);
    return category ? category.slug : '';

};

// Fetch Facets
/* eslint-disable-next-line */
onMounted(async () => {

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('stage')) siteMode.value = urlParams.get('stage');
    if (urlParams.has('query')) searchQuery.value = urlParams.get('query');

    const categoryID = getCatIDFromURL();
    if (categoryID) selectedCategory.value = categoryID;

    if (urlParams.has('region')) selectedRegion.value = urlParams.get('region');
    if (urlParams.has('page')) currentPage.value = urlParams.get('page');

    regionFilters.forEach((filter) => {

        regionInfo[filter.region] = filter.title;

    });

    try {

        // initial fetch
        /* eslint-disable-next-line */
        fetchResults();

        const { facets } = await searchIndex.search('', {
            facets: ['objectCategories'],
            hitsPerPage: 1,
        });

        categoriesFacets.value = categoryFacets(facets.objectCategories || []);
        regionsFacets.value = regionFilters;

        /* eslint-disable-next-line */
        updateBlogTitleAndSEO();

    } catch (error) {

        /* eslint-disable-next-line */
        console.error('Error fetching facets:', error);

    }

});

const urlSearchParams = computed(() => {

    const params = new URLSearchParams();
    if (siteMode.value) params.set('stage', siteMode.value);
    if (searchQuery.value) params.set('query', searchQuery.value);
    if (selectedRegion.value) params.set('region', selectedRegion.value);
    if (currentPage.value > 1) params.set('page', currentPage.value);
    return params;

});

// Watch for Changes in Filters and Fetch Results
watch([currentPage, selectedCategory, selectedRegion, searchQuery, layoutType], (
    [newCurrentPage, newSelectedCategory, newSelectedRegion, newSearchQuery, newLayoutType],
    [oldCurrentPage, oldSelectedCategory, oldSelectedRegion, oldSearchQuery, oldLayoutType],
) => {

    // reset page number if search query changes
    if (newCurrentPage === oldCurrentPage) {

        currentPage.value = 1;

    }

    /* eslint-disable-next-line */
    fetchResults();

});

const clearFilters = () => {

    searchQuery.value = '';
    selectedCategory.value = '';
    selectedRegion.value = '';

};

const updateBlogTitleAndSEO = () => {

    if (!categoriesFacets.value.length) return;
    let seoData = pageSeoData;
    if (selectedCategory.value) {

        const category = categoriesFacets.value.find((c) => c.category === selectedCategory.value);
        if (blogTitleDom && category) {

            blogTitleDom.innerHTML = category.title;
            if (blogTaglineDom) {

                blogTaglineDom.innerHTML = originalBlogTitle;
                blogTaglineDom.classList.remove('hidden', 'invisible');

            }

        }
        if (category) {

            seoData = category.seo;

        }

    } else if (blogTitleDom) {

        blogTitleDom.innerHTML = originalBlogTitle;
        if (blogTaglineDom) {

            blogTaglineDom.innerHTML = '';
            blogTaglineDom.classList.add('hidden', 'invisible');

        }

    }
    useHead(seoData);

};

// Fetch Results
/* eslint-disable-next-line */
const fetchResults = async () => {

    isLoading.value = true;

    // Construct the new URL
    const baseUrl = getBlogBaseURI();

    const categorySlug = getCatSlugFromID();
    const categoryParam = categorySlug ? `category/${categorySlug}/` : '';
    let queryParams = urlSearchParams.value.toString() ? `?${urlSearchParams.value.toString()}` : '';
    const utmParams = getUtmParams();
    if (utmParams.length) {

        const utmString = utmParams.map((param) => `${param.key}=${param.value}`).join('&');
        queryParams = queryParams ? `${queryParams}&${utmString}` : `?${utmString}`;

    }

    const newUrl = baseUrl + categoryParam + queryParams;

    updateBlogTitleAndSEO();

    // Update the browser's URL
    window.history.pushState({}, '', newUrl);
    try {

        const filters = [];
        filters.push('objectTags:illinois-makers');

        if (selectedCategory.value) {

            filters.push(`objectCategories:${selectedCategory.value}`);

        }

        if (selectedRegion.value) {

            filters.push(`objectRegion:${selectedRegion.value}`);

        }

        const { hits, nbPages, nbHits } = await searchIndex.search(searchQuery.value, {
            filters: filters.join(' AND '),
            page: currentPage.value - 1,
            hitsPerPage: layoutType.value === 'grid' ? 15 : 24, // Adjust as needed
        });

        results.value = hits;
        totalPages.value = nbPages;
        totalResults.value = nbHits;
        isLoading.value = false;

    } catch (error) {

        /* eslint-disable-next-line */
        console.error('Error fetching results:', error);

    }

};

</script>
